/**
 * The customer app (hand-off 2026-10-11). Providers, then the router.
 *
 * One React Query client — @inferrex/api-client's — so the client's own invalidations (switching
 * workspace or environment refetches everything) reach every page. Auth is the generated provider
 * chosen at build time by VITE_AUTH_PROVIDER: Entra on hosted, Inferrex email/password + MFA on
 * self-hosted.
 */
import { useMemo } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { ErrorBoundary, Toaster, useThemePreference } from '@inferrex/ui';
import { AuthProvider } from '@inferrex/ui/auth';
import { queryClient } from '@inferrex/api-client';
import { installErrorHandling } from './lib/query-errors';
import { buildRoutes } from './router';

installErrorHandling(queryClient);

/** Applies the remembered theme (light, dark, or the device's) before anything paints. */
function ThemeRoot() {
  useThemePreference();
  return null;
}

export function App() {
  const router = useMemo(() => createBrowserRouter(buildRoutes()), []);
  return (
    <ErrorBoundary fallbackHref="/" fallbackLabel="Go to Home">
      <QueryClientProvider client={queryClient}>
        <ThemeRoot />
        <AuthProvider>
          <RouterProvider router={router} />
          <Toaster position="bottom-right" richColors />
        </AuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
